import axios from 'axios';
import { useEffect, useState } from 'react';
import { B3 } from '../utils/RoutesApi';

// Maior que o pior caso do backend (espera pelo slot compartilhado da B3 + a
// chamada externa). Com 30s o axios abortava antes de o servidor responder e a
// tela passava a mostrar "desconectado" sem que a B3 tivesse dito nada.
const REQUEST_TIMEOUT = 45000;

// O backend cacheia a confirmação por 15min; consultar de minuto em minuto só
// gerava carga no rate-limit de 1 req/s que é compartilhado com os jobs de
// importação de carteira.
const POLL_INTERVAL = 300000;

export function useB3Connection(user, setUser) {
  // Nunca restaurar uma confirmação de conexão do localStorage.
  // `connected === null` significa desconhecido: carregando, ou API/B3
  // indisponível. Nunca deve ser apresentado como "não conectado".
  const [connected, setConnected] = useState(null);
  const [checking, setChecking] = useState(true);
  // 'confirmed' = a B3 respondeu agora | 'stale' = última confirmação dela
  // | 'unknown' = sem resposta e sem histórico utilizável.
  const [status, setStatus] = useState(null);
  const { id, cpf, carteira, automatico } = user;

  // Só zera a confirmação quando o usuário muda de fato. Zerar a cada
  // reexecução do efeito fazia a tela piscar "não confirmada" toda vez que o
  // próprio hook atualizava `carteira`/`automatico`.
  useEffect(() => {
    setConnected(null);
    setStatus(null);
    setChecking(true);
  }, [id, cpf]);

  useEffect(() => {
    let active = true;
    let pending = false;

    async function refresh() {
      if (document.hidden || pending) return;
      pending = true;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}${B3.VALIDATE_LINK_B3}`,
          { timeout: REQUEST_TIMEOUT }
        );
        if (!active) return;
        // `linkB3: null` é indisponibilidade declarada pelo backend: preserva a
        // última confirmação conhecida em vez de derrubar o estado da tela.
        // Quando vem booleano, o backend já resolveu entre resposta ao vivo
        // ('confirmed') e última confirmação da própria B3 ('stale') — os dois
        // são confiáveis, e nenhum deriva de flag local do usuário.
        if (typeof data.linkB3 === 'boolean') setConnected(data.linkB3);
        if (data.linkB3Status) setStatus(data.linkB3Status);
        if (data.user) {
          setUser((current) => {
            if (
              current.id !== id ||
              current.cpf !== cpf ||
              current.carteira !== carteira ||
              current.automatico !== automatico
            )
              return current;
            if (
              current.carteira === data.user.carteira &&
              current.automatico === data.user.automatico
            )
              return current;
            return {
              ...current,
              carteira: data.user.carteira,
              automatico: data.user.automatico,
            };
          });
        }
      } catch {
        // Timeout/queda de rede também preservam o último estado conhecido:
        // uma falha transitória não é prova de que o usuário se desconectou.
      } finally {
        pending = false;
        if (active) setChecking(false);
      }
    }

    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [id, cpf, carteira, automatico, setUser]);

  return { connected, checking, status };
}
